import i18n from "@/i18n";
/**
 * WorkforceSchedule — Prompt 12E + Prompt 12G-3.
 *
 * 21-day Schedule grid for the active MiseOne Location. Loads the
 * canonical Period via `GET /api/workforce/periods/current` anchored
 * on the browser-local calendar day, then loads
 * `GET /api/workforce/schedule` for that Period.
 *
 * Prompt 12G-3 layers direct Shift editing on top: it owns the
 * autosave POST / PATCH / DELETE requests, tracks one pending
 * mutation per logical (employee_id, date) cell, disables Period
 * navigation while any Shift mutation is pending, and reconciles
 * canonical Schedule state through the same request-identity guard
 * that governs read navigation.
 *
 * Explicitly deferred to Prompt 12G-4: navigation-triggered blur
 * cancellation, final Tab / Shift+Tab focus movement, full stale
 * mutation-context hardening. Day-Entry writes and multi-Shift
 * (split) creation remain out of scope.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import {
    browserLocalYMD,
    formatPeriodRange,
} from "@/components/workforce/workforceHelpers";
import ScheduleGrid from "@/components/workforce/ScheduleGrid";
import { planFill, fillFailureReason, describeNotCopied } from "@/components/workforce/shiftFill";
import { ScheduleFillConfirm } from "@/components/workforce/ScheduleFillConfirm";
import CopyLastWeekDialog from "@/components/workforce/CopyLastWeekDialog";
import FirstScheduleSetupSheet from
    "@/components/workforce/FirstScheduleSetupSheet";
import PeriodCloseControl from
    "@/components/workforce/PeriodCloseControl";
import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAbsenceActions } from "@/components/workforce/useAbsenceActions";

const _PERIOD_ANCHOR_CODE = "WORKFORCE_PERIOD_ANCHOR_REQUIRED";
const _ANCHOR_LOCKED_CODE = "WORKFORCE_PERIOD_ANCHOR_LOCKED";
// Prompt 12L-1B §6 · §15 · §16 — manager-facing setup copy.
const _SETUP_HEADING = "Set up Workforce schedule";
const _SETUP_SUB =
    "Choose when this location's first 3-week schedule starts.";
const _SETUP_CTA = "Set up schedule";
const _SETUP_ALREADY_MSG =
    "This location's Workforce schedule has already been set up.";
const _SETUP_MONDAY_MSG = "Choose a Monday for the first schedule.";
const _SETUP_GENERIC_MSG = "Could not set up the schedule. Try again.";
const _GENERIC_MESSAGE = "Could not load the schedule.";
const _EMPTY_MESSAGE = "No employees in this schedule period.";

// Prompt 12M-F §3 · §6 · §9 · §11 · §12 — verbatim manager-facing
// Publish copy. Kept as module constants so tests can assert exact
// equality and future changes are visible.
const _PUBLISH_CTA = "Publish";
const _PUBLISH_DIALOG_TITLE = "Publish schedule?";
const _PUBLISH_DIALOG_BODY =
    "Employees will see this published version when employee access "
    + "is enabled. You can still edit the current schedule and publish "
    + "a new version later.";
const _PUBLISH_DIALOG_CONFIRM = "Publish";
const _PUBLISH_DIALOG_CANCEL = "Cancel";
const _PUBLISH_EMPTY_MSG =
    "Add at least one shift or day code before publishing.";
const _PUBLISH_MISSING_EMPLOYEE_MSG =
    "One or more employees in this schedule could not be found. "
    + "Refresh the schedule and try again.";
const _PUBLISH_GENERIC_MSG =
    "Could not publish the schedule. Try again.";
const _PUBLISH_EMPTY_CODE = "WORKFORCE_PUBLISH_EMPTY_PLAN";
const _PUBLISH_MISSING_CODE =
    "WORKFORCE_PUBLISH_EMPLOYEE_SNAPSHOT_INCOMPLETE";

// Prompt 12M-F §4 — client-side visibility of the Publish action
// mirrors the backend's authorized-manager set. Backend remains
// authoritative; this Set only decides whether to render the button.
const _PUBLISH_AUTHORIZED_ROLES = new Set([
    "restaurant", "restaurant_admin", "corporate",
]);

// Prompt 12G-3 §38 · §39 · §40 · §41 — exact manager-facing toast
// text for each machine-coded mutation error. Kept as constants so
// tests can assert verbatim equality and future changes are visible.
const _MSG_REVISION_CONFLICT = "Schedule changed. Please try again.";
const _MSG_OVERLAP =
    "This employee already has an overlapping shift.";
const _MSG_NOT_APPLICABLE =
    "This employee cannot be scheduled in this location on this date.";
const _MSG_GENERIC_MUTATION = "Could not save the shift. Try again.";
// Prompt 12K-3 §25 — exact manager-facing toast for Plan-Cell
// exclusivity conflicts. Frozen copy; do NOT change punctuation.
const _MSG_PLAN_CELL_CONFLICT =
    "Remove the existing shift or day code first.";

// Prompt 17B2 §16 — verbatim manager-facing copy for the canonical
// 409 WORKFORCE_PERIOD_CLOSED mutation error. Frozen so tests can
// assert exact equality.
const _MSG_PERIOD_CLOSED =
    "This period is closed. Reopen it before making changes.";
const _READONLY_BANNER_TEXT = "Period closed";
const _READONLY_BANNER_HINT = "Reopen the period to make changes.";

// Prompt 12G-3 §15 — collision-safe logical cell key.
function _cellKey(employeeId, date) {
    return JSON.stringify([employeeId, date]);
}

import SupportAssignmentsPanel from "@/components/workforce/SupportAssignmentsPanel";
import { canManageAbsences } from "@/components/workforce/absenceShared";

export default function WorkforceSchedule() {
    const { restaurantId, user } = useApp();

    // Prompt 12E §6 — the two backend calls together own all Period
    // state. `period` is null until resolved; `schedule` is null
    // until fetched.
    const [period, setPeriod] = useState(null);
    const [schedule, setSchedule] = useState(null);
    const [loading, setLoading] = useState(false);
    // "generic" | "anchor" | null
    const [errorKind, setErrorKind] = useState(null);
    // Prompt 12L-1B §6-§13 — first-use setup state (WorkforceSchedule
    // owns the network call, per §4 / §11). `setupOpen` toggles the
    // Sheet; `setupPending` prevents duplicate submits while the
    // POST is in flight. The actual submit handler is declared
    // AFTER `loadForDate` to avoid a temporal-dead-zone reference
    // to that later `useCallback` binding.
    const [setupOpen, setSetupOpen] = useState(false);
    const [setupPending, setSetupPending] = useState(false);


    // Prompt 12E §13 — request-identity guard.
    const reqIdRef = useRef(0);

    // Prompt 12G-4 §8 · §11 · §13 · §14 — parent-controlled cancel
    // signal broadcast to every editor cell. Advanced on
    // Previous/Next pointer-down and on Location/Period change.
    const [editCancelVersion, setEditCancelVersion] = useState(0);
    const bumpEditCancel = useCallback(
        () => setEditCancelVersion((v) => v + 1), []);

    // Prompt 12G-4 §15 — monotonic context generation used to reject
    // stale mutation results. Advanced on restaurantId or period_id
    // change. Kept in a ref so mutation captures do not race with
    // state batching.
    const contextVersionRef = useRef(0);
    // Prompt 12G-4 §21 — mutation-instance tokens per pending cell
    // key so a late completion cannot clear a newer mutation for the
    // same key.
    const pendingTokensRef = useRef(new Map()); // key -> token

    const clearAll = useCallback(() => {
        setPeriod(null);
        setSchedule(null);
        setErrorKind(null);
    }, []);

    // Prompt 16B-2B1 §3 · §4 · §14 — lazy per-employee Averaging
    // summary cache. Populated on first render of a Day Entry
    // cell for an employee; reuses the existing manager summary
    // endpoint (no new endpoint). `null` = fetch in flight or
    // ineligible (STANDARD / unsupported scheme). A non-null
    // entry carries the resolved `{ cycle, eligible }` view.
    const [transferCache, setTransferCache] = useState({});
    const transferInFlightRef = useRef(new Set());

    const getTransferContextForEmployee = useCallback(
        (employeeId) => {
            if (!employeeId) return null;
            const cached = transferCache[employeeId];
            if (cached !== undefined) {
                return cached && cached.eligible ? cached : null;
            }
            if (transferInFlightRef.current.has(employeeId)) return null;
            transferInFlightRef.current.add(employeeId);
            (async () => {
                try {
                    const resp = await api.get(
                        `/workforce/employees/${employeeId}/averaging`);
                    const data = resp?.data || {};
                    const eligible = (
                        data.working_time_model === "AVERAGING"
                            && data.scheme === "TES12_1_STANDARD"
                            && data.current_cycle
                            && data.current_cycle.averaging_cycle_id);
                    const entry = eligible
                        ? { eligible: true, cycle: data.current_cycle }
                        : { eligible: false, cycle: null };
                    setTransferCache((prev) => ({
                        ...prev, [employeeId]: entry,
                    }));
                } catch (_e) {
                    setTransferCache((prev) => ({
                        ...prev,
                        [employeeId]: { eligible: false, cycle: null },
                    }));
                } finally {
                    transferInFlightRef.current.delete(employeeId);
                }
            })();
            return null;
        },
        [transferCache]);

    // Resolve a Period for the currently-active Location and given
    // anchor date, then fetch its Schedule. §5 / §8 / §9 / §12.
    const loadForDate = useCallback(async (dateYmd) => {
        if (!restaurantId) return;
        const rid = restaurantId;
        const myReq = ++reqIdRef.current;
        setLoading(true);
        setErrorKind(null);
        // §35 — clear rows so old data is not visible during the
        // navigation transition.
        setSchedule(null);
        setPeriod(null);
        try {
            const { data: periodData } = await api.get(
                "/workforce/periods/current",
                { params: { restaurant_id: rid, date: dateYmd } },
            );
            // §13 — abandon stale responses.
            if (myReq !== reqIdRef.current) return;
            setPeriod(periodData);

            const { data: scheduleData } = await api.get(
                "/workforce/schedule",
                {
                    params: {
                        restaurant_id: rid,
                        period_id: periodData.period_id,
                    },
                },
            );
            if (myReq !== reqIdRef.current) return;
            // Prompt 12G-4 §14 · §15 — advance context generation on
            // successful Period/Schedule resolution so any pending
            // mutation captured under an older context becomes stale.
            contextVersionRef.current += 1;
            // Prompt 12G-4 §22 — clear old visible Saving… indicators
            // that belonged to the previous Location/Period. Old
            // network requests may still complete asynchronously but
            // are governed by the stale rules below.
            setPendingMutations(new Set());
            pendingTokensRef.current = new Map();
            setSchedule(scheduleData);
            // Prompt 18B1 §12 · §15 · §16 — bounded absence fetch
            // for the visible period. One request per period (not
            // per employee/day) that maps absence rows into a
            // client-side per-employee lookup. Failures are
            // non-fatal — Schedule remains usable.
            try {
                const { data: absData } = await api.get(
                    "/workforce/absences",
                    { params: {
                        restaurant_id: restaurantId,
                        start_date: periodData.start_date,
                        end_date: periodData.end_date,
                    } });
                if (myReq === reqIdRef.current) {
                    const map = {};
                    for (const a of (absData?.absences || [])) {
                        (map[a.employee_id] =
                            map[a.employee_id] || []).push(a);
                    }
                    setAbsencesByEmployee(map);
                }
            } catch {
                if (myReq === reqIdRef.current) {
                    setAbsencesByEmployee({});
                }
            }
            // TES-B2C1B2 — reuse the existing Review Hours read model
            // (rows carry backend `vv_context`). Non-fatal.
            try {
                const { data: rh } = await api.get("/workforce/review-hours", {
                    params: { restaurant_id: restaurantId,
                        period_id: periodData.period_id } });
                if (myReq === reqIdRef.current) {
                    const vmap = {};
                    for (const row of (rh?.employees || [])) {
                        if (row.vv_context) vmap[row.employee_id] = row.vv_context;
                    }
                    setVvContextByEmployee(vmap);
                }
            } catch {
                if (myReq === reqIdRef.current) setVvContextByEmployee({});
            }
        } catch (err) {
            if (myReq !== reqIdRef.current) return;
            const code = err?.response?.data?.detail?.code;
            if (code === _PERIOD_ANCHOR_CODE) {
                setErrorKind("anchor");
            } else {
                setErrorKind("generic");
            }
            setPeriod(null);
            setSchedule(null);
        } finally {
            if (myReq === reqIdRef.current) setLoading(false);
        }
    }, [restaurantId]);

    // §5 / §12 — initial load + Location change.
    useEffect(() => {
        // Prompt 12G-4 §13 — Location switch bumps the cancel signal
        // so any active editor in the old Location cannot flush a
        // deferred blur into the new Location's Schedule state.
        bumpEditCancel();
        clearAll();
        if (!restaurantId) return;
        loadForDate(browserLocalYMD());
    }, [restaurantId, clearAll, loadForDate, bumpEditCancel]);

    // Prompt 12L-1B §11 · §13 · §15 · §16 · §17 — page-owned setup
    // POST. Declared AFTER `loadForDate` to keep the `useCallback`
    // dependency reference within TDZ scope.
    const handleFirstSetupSubmit = useCallback(async (anchorYmd) => {
        if (!restaurantId || !anchorYmd || setupPending) return;
        setSetupPending(true);
        try {
            await api.post("/workforce/periods/anchor", {
                restaurant_id: restaurantId,
                anchor_date: anchorYmd,
            });
            // §13 — reconcile via the canonical Schedule reload
            // path rather than trusting the POST response as final
            // page state. §14 — same-anchor idempotent success
            // flows through here too.
            setSetupOpen(false);
            setErrorKind(null);
            loadForDate(browserLocalYMD());
        } catch (err) {
            const code = err?.response?.data?.detail?.code;
            if (code === _ANCHOR_LOCKED_CODE) {
                // §15 — another authorized manager already set up
                // this location. Do NOT expose the code; show
                // manager-facing feedback and canonical reload.
                toast.error(i18n.t("wf.WorkforceSchedule._SETUP_ALREADY_MSG", _SETUP_ALREADY_MSG));
                setSetupOpen(false);
                setErrorKind(null);
                loadForDate(browserLocalYMD());
            } else if (err?.response?.status === 422) {
                // §16 — backend date validation. Keep Sheet open,
                // do not auto-snap.
                toast.error(i18n.t("wf.WorkforceSchedule._SETUP_MONDAY_MSG", _SETUP_MONDAY_MSG));
            } else {
                // §17 — other errors. Keep Sheet open for retry.
                toast.error(i18n.t("wf.WorkforceSchedule._SETUP_GENERIC_MSG", _SETUP_GENERIC_MSG));
            }
        } finally {
            setSetupPending(false);
        }
    }, [restaurantId, setupPending, loadForDate]);

    // Prompt 12G-4 §11 · §12 — Previous/Next pointer-down handler.
    // Runs before the Button's synthetic click / focus-out, so the
    // active editor's deferred blur commit is canceled before it can
    // fire. The Button `onClick` still performs the actual navigation
    // and continues to consume the existing backend previous/next
    // Period identifiers.
    const cancelBeforeNav = useCallback(() => {
        bumpEditCancel();
    }, [bumpEditCancel]);

    // §8 / §9 — Period navigation.
    const goPrevious = useCallback(() => {
        if (!period || loading) return;
        loadForDate(period.previous_start_date);
    }, [period, loading, loadForDate]);
    const goNext = useCallback(() => {
        if (!period || loading) return;
        loadForDate(period.next_start_date);
    }, [period, loading, loadForDate]);

    // §38 — Retry.
    const retry = useCallback(() => {
        if (period) {
            loadForDate(period.start_date);
        } else {
            loadForDate(browserLocalYMD());
        }
    }, [period, loadForDate]);

    // ── Prompt 12M-F · Publish schedule ─────────────────────────
    // §6 · §8 · §9 — confirmation modal + single-flight guard.
    // The dialog does NOT auto-close on the AlertDialogAction click;
    // the handler explicitly closes it after the request settles so
    // §8 (disable primary + prevent double-submit) and §9 (close on
    // 201 then reload the current schedule) both remain correct.
    const [publishDialogOpen, setPublishDialogOpen] = useState(false);
    const [publishPending, setPublishPending] = useState(false);
    // Prompt 18B2-P1 §22 — after this session has successfully
    // published a given period, its Publish CTA becomes "Republish"
    // so managers clearly understand a subsequent publish creates a
    // new immutable snapshot (backend already versions them; this is
    // pure UX). Session-local; refetched-safe.
    const [republishedPeriodIds, setRepublishedPeriodIds] = useState(
        () => new Set());
    // Prompt 18B1 §12 · §15 · §16 — bounded per-employee absence
    // map for the visible period. Keyed by employee_id → array of
    // ACTIVE absence rows overlapping [period.start_date,
    // period.end_date]. Rendered inside the Schedule cell body so
    // managers can see absence context without leaving Schedule.
    const [absencesByEmployee, setAbsencesByEmployee] = useState({});
    // TES-B2C1B2 — canonical VV context per employee (one request).
    const [vvContextByEmployee, setVvContextByEmployee] = useState({});

    // Prompt 17B2 §5 · §6 · §16 — current Period Close state as
    // hydrated by PeriodCloseControl. `null` = unknown/loading;
    // otherwise a compact projection carrying `current_state`
    // ("OPEN"|"CLOSED") and `latest_close_version`. When CLOSED the
    // page becomes read-only and Publish is disabled.
    const [closeState, setCloseState] = useState(null);
    const periodClosed = closeState && closeState.current_state === "CLOSED";
    // CALENDAR-ABSENCES-V1 — manager-only absence add / edit / cancel from the calendar.
    const absenceUi = useAbsenceActions({
        user, restaurantId, view: "schedule",
        employees: schedule?.employees, locked: Boolean(periodClosed),
        onChanged: () => period && loadForDate(period.start_date),
    });

    // §4 — client-side visibility mirrors backend's authorized set.
    const canPublish = !!(
        user && _PUBLISH_AUTHORIZED_ROLES.has(user.role));

    const handlePublishConfirm = useCallback(async () => {
        // §5 · §8 — active restaurant + current period + no double
        // submit. Guard against clicks that arrive while a request
        // is still in flight.
        if (!restaurantId || !period || publishPending) return;
        const rid = restaurantId;
        const pid = period.period_id;
        setPublishPending(true);
        try {
            // §7 — POST /api/workforce/periods/{period_id}/publish
            // with the active restaurant_id supplied via the
            // canonical `params` mechanism (no request body). Actor
            // identity + publication_version are NEVER sent from
            // the client (§5 / §7 / §10).
            const { data } = await api.post(
                `/workforce/periods/${pid}/publish`, null,
                { params: { restaurant_id: rid } },
            );
            // §9 — close, success toast with API-returned version,
            // canonical current-Schedule reload. Current Schedule
            // remains editable per §15.
            setPublishDialogOpen(false);
            toast.success(
                `Schedule published — version ${data.publication_version}`);
            // Prompt 18B2-P1 §22 — remember that this period has now
            // been published in this session so the CTA switches to
            // "Republish".
            setRepublishedPeriodIds((prev) => {
                const next = new Set(prev);
                next.add(pid);
                return next;
            });
            loadForDate(period.start_date);
        } catch (err) {
            const code = err && err.response && err.response.data
                ? err.response.data.detail : undefined;
            const status = err && err.response
                ? err.response.status : undefined;
            if (status === 409 && code === _PUBLISH_EMPTY_CODE) {
                // §11 — empty plan.
                toast.error(i18n.t("wf.WorkforceSchedule._PUBLISH_EMPTY_MSG", _PUBLISH_EMPTY_MSG));
            } else if (
                status === 409 && code === _PUBLISH_MISSING_CODE
            ) {
                // §12 — missing referenced employee.
                toast.error(i18n.t("wf.WorkforceSchedule._PUBLISH_MISSING_EMPLOYEE_MSG", _PUBLISH_MISSING_EMPLOYEE_MSG));
            } else {
                // §13 — canonical opaque failure. Do NOT expose the
                // raw error; do NOT show success.
                toast.error(i18n.t("wf.WorkforceSchedule._PUBLISH_GENERIC_MSG", _PUBLISH_GENERIC_MSG));
            }
            setPublishDialogOpen(false);
        } finally {
            setPublishPending(false);
        }
    }, [restaurantId, period, publishPending, loadForDate]);

    // ── Prompt 12G-3 · Shift mutation autosave state ────────────
    // Prompt 12G-3 §15 · §16 · §17 — one pending Shift mutation per
    // logical (employee_id, date) cell. Other cells stay editable.
    const [pendingMutations, setPendingMutations] = useState(
        () => new Set(),
    );
    const anyShiftMutationPending = pendingMutations.size > 0;

    // Prompt 12G-3 §11 — pending query for ScheduleGrid / editor.
    const isShiftCellPending = useCallback(
        (employeeId, date) =>
            pendingMutations.has(_cellKey(employeeId, date)),
        [pendingMutations],
    );

    // Prompt 12G-3 §29 · §30 · §31 · §36 · §42 · §51 · §55 — canonical
    // post-mutation reconciliation using the SAME restaurant_id +
    // period_id currently in view. Reuses the Prompt-12E request
    // identity so stale responses can never overwrite fresh Schedule
    // state.
    const [copyLastWeekEmp, setCopyLastWeekEmp] = useState(null);
    const reconcileSchedule = useCallback(async () => {
        if (!restaurantId || !period) return false;
        const rid = restaurantId;
        const pid = period.period_id;
        const myReq = ++reqIdRef.current;
        setLoading(true);
        setErrorKind(null);
        try {
            const { data } = await api.get(
                "/workforce/schedule",
                { params: { restaurant_id: rid, period_id: pid } },
            );
            if (myReq !== reqIdRef.current) return false;
            setSchedule(data);
            return true;
        } catch (_err) {
            if (myReq !== reqIdRef.current) return false;
            setErrorKind("generic");
            setSchedule(null);
            return false;
        } finally {
            if (myReq === reqIdRef.current) setLoading(false);
        }
    }, [restaurantId, period]);

    // Prompt 12G-3 §37 — canonical error-code extraction. Backend
    // Prompt-12D returns machine codes on `detail.code`.
    const _errorCode = (err) =>
        err && err.response && err.response.data
            && err.response.data.detail
            ? err.response.data.detail.code
            : undefined;

    const _showMutationErrorToast = (code) => {
        if (code === "WORKFORCE_REVISION_CONFLICT") {
            toast.error(i18n.t("wf.WorkforceSchedule._MSG_REVISION_CONFLICT", _MSG_REVISION_CONFLICT));
        } else if (code === "WORKFORCE_SHIFT_OVERLAP") {
            toast.error(i18n.t("wf.WorkforceSchedule._MSG_OVERLAP", _MSG_OVERLAP));
        } else if (code === "WORKFORCE_SCHEDULE_NOT_APPLICABLE") {
            toast.error(i18n.t("wf.WorkforceSchedule._MSG_NOT_APPLICABLE", _MSG_NOT_APPLICABLE));
        } else if (code === "WORKFORCE_PLAN_CELL_CONFLICT") {
            // Prompt 12K-3 §25 — Plan-Cell exclusivity conflict.
            toast.error(i18n.t("wf.WorkforceSchedule._MSG_PLAN_CELL_CONFLICT", _MSG_PLAN_CELL_CONFLICT));
        } else if (code === "WORKFORCE_X_TRANSFER_MAX_EXCEEDED") {
            // Prompt 16B-2B1 §6 — friendly copy for canonical
            // transferred-X error codes surfaced by 16B-2A1.
            toast.error(
                i18n.t("wf.workforceSchedule.maximum2TransferredXDaysFrom", "Maximum 2 transferred X days from this period."));
        } else if (code === "WORKFORCE_X_TRANSFER_INVALID_SCOPE") {
            toast.error(
                i18n.t("wf.workforceSchedule.transferredXMustStayWithinThe", "Transferred X must stay within the same averaging cycle."));
        } else if (code === "WORKFORCE_X_TRANSFER_SCHEME_NOT_SUPPORTED"
                    || code === "WORKFORCE_X_TRANSFER_METADATA_NOT_ALLOWED") {
            toast.error(i18n.t("wf.workforceSchedule.transferredXIsNotAvailableFor", "Transferred X is not available for this employee."));
        } else if (code === "WORKFORCE_X_TRANSFER_CONCURRENCY_UNAVAILABLE") {
            toast.error(
                i18n.t("wf.workforceSchedule.couldNotSaveTransferredXRight", "Could not save transferred X right now. Try again."));
        } else if (code === "WORKFORCE_PERIOD_CLOSED") {
            // Prompt 17B2 §16 — known conflict; manager-safe toast.
            toast.error(i18n.t("wf.WorkforceSchedule._MSG_PERIOD_CLOSED", _MSG_PERIOD_CLOSED));
        } else {
            toast.error(i18n.t("wf.WorkforceSchedule._MSG_GENERIC_MUTATION", _MSG_GENERIC_MUTATION));
        }
    };

    // Prompt WH1A §3 — stabilized mutation-token bookkeeping helpers.
    // Each of these touches ONLY refs and functional setState, so a
    // stable identity across renders is both safe and required to
    // satisfy react-hooks/exhaustive-deps on the three mutation
    // callbacks below without altering runtime semantics.
    const _addPending = useCallback((key) => setPendingMutations((prev) => {
        if (prev.has(key)) return prev;
        const next = new Set(prev);
        next.add(key);
        return next;
    }), []);
    const _removePending = useCallback((key) => setPendingMutations((prev) => {
        if (!prev.has(key)) return prev;
        const next = new Set(prev);
        next.delete(key);
        return next;
    }), []);

    // Prompt 12G-4 §16 · §17 — capture and stale checks. Captured
    // context matches iff every dimension (restaurantId, period_id,
    // scheduleContextVersion) still equals the currently rendered
    // Schedule. Live refs are used so an in-flight async handler sees
    // the CURRENT value rather than its closure-captured value.
    const restaurantIdRef = useRef(restaurantId);
    const periodRef = useRef(period);
    useEffect(() => { restaurantIdRef.current = restaurantId; },
        [restaurantId]);
    useEffect(() => { periodRef.current = period; }, [period]);

    const _captureContext = () => ({
        rid: restaurantIdRef.current,
        pid: periodRef.current ? periodRef.current.period_id : null,
        cv: contextVersionRef.current,
    });
    const _contextStillCurrent = (cap) => (
        cap.rid === restaurantIdRef.current
            && periodRef.current
            && cap.pid === periodRef.current.period_id
            && cap.cv === contextVersionRef.current
    );

    // Prompt 12G-4 §21 · §23 — mutation-instance token per pending
    // cell. Only the mutation that registered the current token may
    // clear it.
    const _registerToken = useCallback((key) => {
        const token = Symbol("mutation-token");
        pendingTokensRef.current.set(key, token);
        return token;
    }, []);
    const _tokenStillCurrent = useCallback((key, token) => (
        pendingTokensRef.current.get(key) === token
    ), []);
    const _clearTokenIfCurrent = useCallback((key, token) => {
        if (_tokenStillCurrent(key, token)) {
            pendingTokensRef.current.delete(key);
            _removePending(key);
        }
    }, [_tokenStillCurrent, _removePending]);

    // Prompt 12G-4 §25 · §29 · §30 · §35 · §43 — one-shot focus
    // request emitted only after a successfully-committed Tab or
    // Shift+Tab mutation whose canonical reconciliation has landed.
    const [focusRequest, setFocusRequest] = useState(null);
    const focusReqIdRef = useRef(0);
    const _emitFocusRequest = (cellKey, direction) => {
        focusReqIdRef.current += 1;
        setFocusRequest({
            request_id: focusReqIdRef.current,
            cell_key: cellKey,
            direction,
        });
    };

    // Prompt 12G-3 §9 · §16 · §19-§23 + Prompt 12G-4 §16-§20 · §29-§35.
    const handleShiftCommitIntent = useCallback(
        async (employeeId, date, intent) => {
            if (!restaurantId || !period) return;
            const key = _cellKey(employeeId, date);
            if (pendingMutations.has(key)) return;
            const captured = _captureContext();
            const token = _registerToken(key);
            _addPending(key);
            let mutationOk = false;
            try {
                if (intent.mode === "create") {
                    await api.post("/workforce/schedule/shifts", {
                        restaurant_id: captured.rid,
                        period_id: captured.pid,
                        employee_id: employeeId,
                        work_date: date,
                        start_time: intent.parsed.start_time,
                        end_date: intent.parsed.end_date,
                        end_time: intent.parsed.end_time,
                    });
                } else if (intent.mode === "update") {
                    const sid = intent.shift.schedule_entry_id;
                    await api.patch(
                        `/workforce/schedule/shifts/${sid}`,
                        {
                            restaurant_id: captured.rid,
                            expected_revision: intent.shift.revision,
                            start_time: intent.parsed.start_time,
                            end_date: intent.parsed.end_date,
                            end_time: intent.parsed.end_time,
                        },
                    );
                } else {
                    _clearTokenIfCurrent(key, token);
                    return;
                }
                mutationOk = true;
                // Prompt 12G-4 §18 · §42 — stale success is silently
                // ignored in the new context.
                if (!_contextStillCurrent(captured)) {
                    _clearTokenIfCurrent(key, token);
                    return;
                }
                // Prompt 12G-3 §29 · §30 — canonical refresh.
                const refreshOk = await reconcileSchedule();
                // Prompt 12G-4 §34 · §47 · §48 — focus movement only
                // after successful reconciliation.
                if (
                    refreshOk
                        && _contextStillCurrent(captured)
                        && (intent.source === "tab"
                            || intent.source === "shift-tab")
                ) {
                    _emitFocusRequest(
                        key,
                        intent.source === "tab" ? "next" : "previous",
                    );
                }
            } catch (err) {
                // Prompt 12G-4 §19 · §54 — stale error is silent.
                if (!_contextStillCurrent(captured)) {
                    _clearTokenIfCurrent(key, token);
                    return;
                }
                if (!mutationOk) {
                    _showMutationErrorToast(_errorCode(err));
                }
                await reconcileSchedule();
            } finally {
                _clearTokenIfCurrent(key, token);
            }
        },
        [
            restaurantId, period, pendingMutations, reconcileSchedule,
            _addPending, _registerToken, _clearTokenIfCurrent,
        ],
    );

    // Prompt 12G-3 §10 · §24 · §25 + Prompt 12G-4 §16-§20 · §33.
    const handleShiftDeleteIntent = useCallback(
        async (employeeId, date, intent) => {
            if (!restaurantId || !period) return;
            const key = _cellKey(employeeId, date);
            if (pendingMutations.has(key)) return;
            const captured = _captureContext();
            const token = _registerToken(key);
            _addPending(key);
            let mutationOk = false;
            try {
                const shift = intent.shift;
                const sid = shift.schedule_entry_id;
                await api.delete(
                    `/workforce/schedule/shifts/${sid}`,
                    {
                        params: {
                            restaurant_id: captured.rid,
                            expected_revision: shift.revision,
                        },
                    },
                );
                mutationOk = true;
                if (!_contextStillCurrent(captured)) {
                    _clearTokenIfCurrent(key, token);
                    return;
                }
                await reconcileSchedule();
                // Prompt 12G-4 §33 — DELETE never requests adjacent
                // focus movement.
            } catch (err) {
                if (!_contextStillCurrent(captured)) {
                    _clearTokenIfCurrent(key, token);
                    return;
                }
                if (!mutationOk) {
                    _showMutationErrorToast(_errorCode(err));
                }
                await reconcileSchedule();
            } finally {
                _clearTokenIfCurrent(key, token);
            }
        },
        [
            restaurantId, period, pendingMutations, reconcileSchedule,
            _addPending, _registerToken, _clearTokenIfCurrent,
        ],
    );

    // Prompt 12G-3 §10 · §16 · §24 · §25 — delete intent handler.
    const handleShiftDeleteIntent_OBSOLETE = null;
    void handleShiftDeleteIntent_OBSOLETE;

    // ── Prompt 12K-3 · Plan-Cell / Day Entry live mutation owner ──
    // Prompt 12K-3 §12 · §41 — WorkforceSchedule is the SINGLE live
    // mutation transport for Plan-Cell intents emitted by K2 editors.
    // ScheduleGrid / PlanCellEditor / DayEntryCellEditor MUST NOT
    // perform HTTP directly. Intents are dispatched on their canonical
    // `mode` string. `create-shift` reuses the EXISTING Shift create
    // pipeline unchanged (§16 · §17). Day Entry mutations map to the
    // three approved routes (§13 · §14 · §15). Every branch reuses the
    // existing per-cell pending / mutation-token / context-freshness
    // guard already used by Shift mutations, so a legacy Shift+Day
    // Entry cell cannot double-mutate.
    const handlePlanCellIntent = useCallback(
        async (employeeId, date, intent) => {
            if (!restaurantId || !period) return;
            if (!intent || typeof intent !== "object") return;
            const key = _cellKey(employeeId, date);
            // Prompt 12K-3 §18 — per-cell pending guard.
            if (pendingMutations.has(key)) return;
            const captured = _captureContext();
            const token = _registerToken(key);
            _addPending(key);
            let mutationOk = false;
            try {
                if (intent.mode === "create-day-entry") {
                    // Prompt 12K-3 §13 — POST Day Entry mapping. No
                    // company_id / revision / source / navigate / sentinel.
                    await api.post(
                        "/workforce/schedule/day-entries",
                        {
                            restaurant_id: captured.rid,
                            period_id: captured.pid,
                            employee_id: employeeId,
                            date,
                            code: intent.code,
                        },
                    );
                } else if (intent.mode === "update-day-entry") {
                    // Prompt 12K-3 §14 — PATCH Day Entry mapping.
                    // Prompt 16B-2B1 §5 — transferred-X provenance
                    // fields are forwarded when the caller supplies
                    // them; explicit `null` clears provenance.
                    const did = intent.dayEntry.day_entry_id;
                    const body = {
                        restaurant_id: captured.rid,
                        expected_revision: intent.dayEntry.revision,
                        code: intent.code,
                    };
                    if (Object.prototype.hasOwnProperty.call(
                            intent, "x_transfer_source_period_id")) {
                        body.x_transfer_source_period_id =
                            intent.x_transfer_source_period_id;
                    }
                    if (Object.prototype.hasOwnProperty.call(
                            intent, "x_transfer_averaging_cycle_id")) {
                        body.x_transfer_averaging_cycle_id =
                            intent.x_transfer_averaging_cycle_id;
                    }
                    await api.patch(
                        `/workforce/schedule/day-entries/${did}`,
                        body,
                    );
                } else if (intent.mode === "delete-day-entry") {
                    // Prompt 12K-3 §15 — DELETE Day Entry mapping.
                    // Query params only; NO JSON body.
                    const did = intent.dayEntry.day_entry_id;
                    await api.delete(
                        `/workforce/schedule/day-entries/${did}`,
                        {
                            params: {
                                restaurant_id: captured.rid,
                                expected_revision: intent.dayEntry.revision,
                            },
                        },
                    );
                } else if (intent.mode === "create-shift") {
                    // Prompt 12K-3 §16 · §17 — reuse EXISTING Shift
                    // create body semantics unchanged.
                    await api.post("/workforce/schedule/shifts", {
                        restaurant_id: captured.rid,
                        period_id: captured.pid,
                        employee_id: employeeId,
                        work_date: date,
                        start_time: intent.parsed.start_time,
                        end_date: intent.parsed.end_date,
                        end_time: intent.parsed.end_time,
                    });
                } else {
                    // Unknown intent mode: clear pending, no-op.
                    _clearTokenIfCurrent(key, token);
                    return;
                }
                mutationOk = true;
                // Prompt 12K-3 §20 — canonical GET is the ONLY
                // authority; skip stale context.
                if (!_contextStillCurrent(captured)) {
                    _clearTokenIfCurrent(key, token);
                    return;
                }
                const refreshOk = await reconcileSchedule();
                // Prompt 12K-3 §20 · §28 · §29 — navigation only after
                // successful mutation + canonical reconciliation.
                if (
                    refreshOk
                        && _contextStillCurrent(captured)
                        && (intent.navigate === "next"
                            || intent.navigate === "previous")
                ) {
                    _emitFocusRequest(key, intent.navigate);
                }
            } catch (err) {
                // Prompt 12K-3 §21 · §27 — stale error silent.
                if (!_contextStillCurrent(captured)) {
                    _clearTokenIfCurrent(key, token);
                    return;
                }
                if (!mutationOk) {
                    _showMutationErrorToast(_errorCode(err));
                }
                // Prompt 12K-3 §21 · §25 · §26 — after any error,
                // reconcile through canonical Schedule GET so visible
                // state matches server truth. Never auto-retry the
                // mutation.
                await reconcileSchedule();
            } finally {
                _clearTokenIfCurrent(key, token);
            }
        },
        [
            restaurantId, period, pendingMutations, reconcileSchedule,
            _addPending, _registerToken, _clearTokenIfCurrent,
        ],
    );

    // Drag-fill: copy one Shift along a row. Sequential writes through the
    // existing routes (backend keeps overlap / closed-period / role checks);
    // one canonical refresh at the end instead of one per copied day.
    const [fillConfirm, setFillConfirm] = useState(null);
    const _reportFill = (ok, total, notCopied) => {
        if (!notCopied.length) {
            toast.success(i18n.t("wf.workforceSchedule.shiftCopied", { count: ok, defaultValue: "Shift copied to {{count}} days." }));
            return;
        }
        toast.error(i18n.t("wf.workforceSchedule.copiedPartial", "Copied to {{ok}} of {{total}} days.", { ok, total }),
            { description: i18n.t("wf.workforceSchedule.notCopied", "Not copied, left unchanged: {{list}}", { list: describeNotCopied(notCopied) }) });
    };
    const runFill = useCallback(async (employeeId, plan) => {
        const captured = _captureContext();
        const keyed = plan.jobs.map((j) => {
            const key = _cellKey(employeeId, j.date);
            _addPending(key);
            return { ...j, key, token: _registerToken(key) };
        });
        const base = { restaurant_id: captured.rid };
        const notCopied = [...plan.skipped];
        let ok = 0;
        for (const j of keyed) {
            if (!_contextStillCurrent(captured)) break;
            try {
                // One request per day: POST into an empty day or atomic
                // revision-CAS PATCH of its single Shift (original kept on failure).
                if (j.kind === "update") {
                    await api.patch(`/workforce/schedule/shifts/${j.shift.schedule_entry_id}`,
                        { ...base, expected_revision: j.shift.revision, ...j.parsed });
                } else {
                    await api.post("/workforce/schedule/shifts", { ...base, period_id: captured.pid,
                        employee_id: employeeId, work_date: j.date, ...j.parsed });
                }
                ok += 1;
            } catch (err) {
                notCopied.push({ date: j.date, reason: fillFailureReason(_errorCode(err)) });
            }
        }
        if (_contextStillCurrent(captured)) {
            await reconcileSchedule();
            notCopied.sort((a, b) => a.date.localeCompare(b.date));
            _reportFill(ok, keyed.length + plan.skipped.length, notCopied);
        }
        keyed.forEach((j) => _clearTokenIfCurrent(j.key, j.token));
    }, [reconcileSchedule, _addPending, _registerToken, _clearTokenIfCurrent]);

    const handleFillIntent = useCallback((employeeId, sourceDate, targetDates) => {
        if (!schedule || publishPending || publishDialogOpen) return;
        const row = schedule.employees.find((e) => e.employee_id === employeeId);
        const plan = planFill(row, sourceDate, targetDates,
            (d) => pendingMutations.has(_cellKey(employeeId, d)));
        if (!plan) return;
        if (!plan.jobs.length) {
            if (plan.skipped.length) _reportFill(0, plan.skipped.length, plan.skipped);
            return;
        }
        if (plan.occupied > 0) {
            setFillConfirm({ employeeId, plan });
            return;
        }
        runFill(employeeId, plan);
    }, [schedule, pendingMutations, runFill, publishPending, publishDialogOpen]);

    const rangeText = useMemo(() => {
        if (!period) return "";
        return formatPeriodRange(period.start_date, period.end_date);
    }, [period]);

    const showLoading = loading;
    const showAnchor = !loading && errorKind === "anchor";
    const showError = !loading && errorKind === "generic";
    const showEmpty = !loading && !errorKind && schedule &&
        Array.isArray(schedule.employees) && schedule.employees.length === 0;
    const showGrid = !loading && !errorKind && schedule &&
        Array.isArray(schedule.employees) && schedule.employees.length > 0;

    return (
        <div data-testid="workforce-schedule-page">
            <div className="px-4 sm:px-6 pt-4">
            <PageHeader
                title={i18n.t("wf.workforceSchedule.schedule", "Schedule")}
                testId="workforce-schedule-header"
                action={
                    <span className="inline-flex items-center gap-3">
                        {showGrid ? absenceUi.toolbar : null}
                        <PeriodCloseControl
                            restaurantId={restaurantId}
                            periodId={period ? period.period_id : null}
                            user={user}
                            onStateChanged={setCloseState}
                        />
                        {canPublish ? (
                            <Button
                                onClick={() => setPublishDialogOpen(true)}
                                disabled={
                                    !period || loading || publishPending
                                        || anyShiftMutationPending
                                        || periodClosed
                                }
                                data-testid="workforce-schedule-publish"
                            >
                                {period && republishedPeriodIds.has(
                                    period.period_id)
                                    ? i18n.t("wf.workforceSchedule.republish", "Republish") : i18n.t("wf.WorkforceSchedule._PUBLISH_CTA", _PUBLISH_CTA)}
                            </Button>
                        ) : null}
                    </span>
                }
            />
            </div>

            <div
                data-testid="workforce-schedule-content"
                className="px-4 sm:px-6 pb-6 space-y-4"
            >
                {/* §10 — compact Period navigation row. */}
                <div
                    data-testid="workforce-schedule-nav"
                    className="flex items-center gap-3"
                >
                    <Button
                        variant="outline"
                        size="sm"
                        onMouseDown={cancelBeforeNav}
                        onClick={goPrevious}
                        disabled={
                            !period || loading || anyShiftMutationPending
                        }
                        data-testid="workforce-schedule-nav-previous"
                    >
                        <ChevronLeft className="h-4 w-4" strokeWidth={2} />
                        {i18n.t("wf.workforceSchedule.previous", "Previous")}
                    </Button>
                    <span
                        data-testid="workforce-schedule-nav-range"
                        className="text-sm text-neutral-700"
                    >
                        {rangeText}
                    </span>
                    <Button
                        variant="outline"
                        size="sm"
                        onMouseDown={cancelBeforeNav}
                        onClick={goNext}
                        disabled={
                            !period || loading || anyShiftMutationPending
                        }
                        data-testid="workforce-schedule-nav-next"
                    >
                        {i18n.t("common.next", "Next")}
                        <ChevronRight className="h-4 w-4" strokeWidth={2} />
                    </Button>
                </div>

                {showLoading && (
                    <div
                        data-testid="workforce-schedule-loading"
                        className="space-y-2"
                    >
                        {[0, 1, 2, 3].map((i) => (
                            <Skeleton
                                key={i}
                                className="h-10 w-full"
                                data-testid={`workforce-schedule-skeleton-${i}`}
                            />
                        ))}
                    </div>
                )}

                {showAnchor && (
                    <div
                        data-testid="workforce-schedule-anchor-required"
                        className="rounded-md border border-neutral-200
                                    bg-white p-8 text-center space-y-4"
                    >
                        <h2
                            className="text-lg font-semibold
                                       text-neutral-900"
                            data-testid="workforce-first-setup-heading"
                        >
                            {i18n.t("wf.WorkforceSchedule._SETUP_HEADING", _SETUP_HEADING)}
                        </h2>
                        <p
                            className="text-sm text-neutral-600"
                            data-testid="workforce-first-setup-sub"
                        >
                            {i18n.t("wf.WorkforceSchedule._SETUP_SUB", _SETUP_SUB)}
                        </p>
                        <div className="pt-2">
                            <Button
                                onClick={() => setSetupOpen(true)}
                                data-testid="workforce-first-setup-cta"
                            >
                                {i18n.t("wf.WorkforceSchedule._SETUP_CTA", _SETUP_CTA)}
                            </Button>
                        </div>
                    </div>
                )}
                <FirstScheduleSetupSheet
                    open={setupOpen}
                    onOpenChange={setSetupOpen}
                    submitting={setupPending}
                    onSubmit={handleFirstSetupSubmit}
                />


                {showError && (
                    <div
                        data-testid="workforce-schedule-error"
                        className="rounded-md border border-neutral-200
                                    bg-white p-6 text-center space-y-3"
                    >
                        <p className="text-sm text-neutral-700">
                            {i18n.t("wf.WorkforceSchedule._GENERIC_MESSAGE", _GENERIC_MESSAGE)}
                        </p>
                        <Button
                            variant="outline"
                            onClick={retry}
                            data-testid="workforce-schedule-retry"
                        >
                            {i18n.t("wf.workforceSchedule.retry", "Retry")}
                        </Button>
                    </div>
                )}

                {(showEmpty || showGrid) && canManageAbsences(user) && (
                    <SupportAssignmentsPanel restaurantId={restaurantId}
                        onChanged={() => period && loadForDate(period.start_date)} />
                )}

                {showEmpty && (
                    <div
                        data-testid="workforce-schedule-empty"
                        className="rounded-md border border-neutral-200
                                    bg-white p-8 text-center"
                    >
                        <p className="text-sm text-neutral-700">
                            {i18n.t("wf.WorkforceSchedule._EMPTY_MESSAGE", _EMPTY_MESSAGE)}
                        </p>
                    </div>
                )}

                {showGrid && (
                    <>
                        {periodClosed && (
                            <div
                                data-testid="workforce-schedule-closed-banner"
                                className="rounded-md border border-neutral-300
                                            bg-neutral-50 px-4 py-2 text-sm
                                            text-neutral-700"
                            >
                                <span className="font-medium">
                                    {i18n.t("wf.WorkforceSchedule._READONLY_BANNER_TEXT", _READONLY_BANNER_TEXT)}
                                </span>
                                <span className="ml-1 text-neutral-500">
                                    {i18n.t("wf.WorkforceSchedule._READONLY_BANNER_HINT", _READONLY_BANNER_HINT)}
                                </span>
                            </div>
                        )}
                        <ScheduleGrid
                            mode="plan"
                            dates={schedule.dates}
                            employees={schedule.employees}
                            onShiftCommitIntent={handleShiftCommitIntent}
                            onShiftDeleteIntent={handleShiftDeleteIntent}
                            onPlanCellIntent={handlePlanCellIntent}
                            isShiftCellPending={isShiftCellPending}
                            cancelEditVersion={editCancelVersion}
                            focusRequest={focusRequest}
                            getTransferContextForEmployee={
                                getTransferContextForEmployee}
                            transferRestaurantId={restaurantId}
                            readOnly={periodClosed || !canManageAbsences(user)}
                            absencesByEmployee={absencesByEmployee}
                            vvContextByEmployee={vvContextByEmployee}
                            onAbsenceOpen={absenceUi.onAbsenceOpen}
                            onFillIntent={periodClosed || !canManageAbsences(user)
                                ? undefined : handleFillIntent}
                            onLastWeekCopyIntent={periodClosed || !canManageAbsences(user)
                                ? undefined : setCopyLastWeekEmp}
                        />
                    </>
                )}
            </div>
            {absenceUi.dialogs}

            <CopyLastWeekDialog
                open={Boolean(copyLastWeekEmp)}
                employee={copyLastWeekEmp}
                restaurantId={restaurantId}
                periodId={period ? period.period_id : null}
                onClose={() => setCopyLastWeekEmp(null)}
                onApplied={reconcileSchedule}
            />
            <ScheduleFillConfirm
                pending={fillConfirm}
                onCancel={() => setFillConfirm(null)}
                onConfirm={() => {
                    const fc = fillConfirm;
                    setFillConfirm(null);
                    if (fc) runFill(fc.employeeId, fc.plan);
                }}
            />

            {/* Prompt 12M-F §6 — Publish confirmation dialog. */}
            <AlertDialog
                open={publishDialogOpen}
                onOpenChange={setPublishDialogOpen}
            >
                <AlertDialogContent
                    data-testid="workforce-publish-dialog"
                >
                    <AlertDialogHeader>
                        <AlertDialogTitle
                            data-testid="workforce-publish-dialog-title"
                        >
                            {i18n.t("wf.WorkforceSchedule._PUBLISH_DIALOG_TITLE", _PUBLISH_DIALOG_TITLE)}
                        </AlertDialogTitle>
                        <AlertDialogDescription
                            data-testid="workforce-publish-dialog-body"
                        >
                            {i18n.t("wf.WorkforceSchedule._PUBLISH_DIALOG_BODY", _PUBLISH_DIALOG_BODY)}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setPublishDialogOpen(false)}
                            disabled={publishPending}
                            data-testid="workforce-publish-dialog-cancel"
                        >
                            {i18n.t("wf.WorkforceSchedule._PUBLISH_DIALOG_CANCEL", _PUBLISH_DIALOG_CANCEL)}
                        </Button>
                        <Button
                            onClick={handlePublishConfirm}
                            disabled={publishPending || anyShiftMutationPending}
                            data-testid="workforce-publish-dialog-confirm"
                        >
                            {i18n.t("wf.WorkforceSchedule._PUBLISH_DIALOG_CONFIRM", _PUBLISH_DIALOG_CONFIRM)}
                        </Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
